/**
 * ブラウザの自動翻訳（Google翻訳など）が原因のDOMエラーの判定と復帰処理
 *
 * 翻訳機能はテキストノードを <font> 要素に置き換えるため、React が保持している
 * 元のテキストノードへの参照が親から外れ、再描画時の insertBefore / removeChild が
 * NotFoundError で失敗する（facebook/react#11538）
 */

// 復帰用の再読み込みであることを次回起動時に伝えるためのキー
const RECOVERY_KEY = 'takutore_translation_recovery';

/**
 * DOMの子ノード不整合を示すメッセージの断片
 *
 * ブラウザごとに文面が異なるため複数持つ
 * - Chrome: Failed to execute 'insertBefore' on 'Node': The node before which
 *           the new node is to be inserted is not a child of this node.
 * - WebKit(Safari/iOS): The node to be removed is not a child of this node.
 * - Firefox: Node.insertBefore: Child to insert before is not a child of this node
 */
const DOM_CHILD_MISMATCH_PATTERNS = [
  'child of this node',
  'insertBefore',
  'removeChild'
];

/**
 * DOMの子ノード不整合エラーかどうかを判定する
 * @param {unknown} error - 発生したエラー
 * @returns {boolean} 子ノード不整合エラーの場合 true
 */
export const isDomChildMismatchError = (error) => {
  if (!error || error.name !== 'NotFoundError') return false;
  const message = error.message || '';
  return DOM_CHILD_MISMATCH_PATTERNS.some((pattern) => message.includes(pattern));
};

/**
 * 現在のページが自動翻訳されているかを判定する
 *
 * Chrome は翻訳時に <html> へ translated-ltr / translated-rtl クラスを付与し、
 * テキストノードを <font> 要素に置き換える
 *
 * @returns {boolean} 翻訳されている場合 true
 */
export const isPageTranslated = () => {
  if (typeof document === 'undefined') return false;
  const html = document.documentElement;
  if (!html) return false;
  if (html.classList.contains('translated-ltr') || html.classList.contains('translated-rtl')) {
    return true;
  }
  return document.querySelector('font') !== null;
};

/**
 * 自動翻訳が原因のDOMエラーか判定する
 *
 * メッセージだけで判定すると、サードパーティスクリプト（GTMのタグなど）や
 * PDFビューアの後片付けで出る同種のエラーを誤って翻訳のせいにしてしまうため、
 * 実際にページが翻訳されているかも合わせて確認する
 *
 * @param {unknown} error - 発生したエラー
 * @returns {boolean} 自動翻訳が原因と推定される場合 true
 */
export const isTranslationDomError = (error) =>
  isDomChildMismatchError(error) && isPageTranslated();

/**
 * 翻訳を無効にした状態で再読み込みする
 *
 * 単に reload すると同じ条件で再度翻訳されて同じ場所で落ちるため、
 * 次回起動時にページ全体を翻訳対象外にする印を残してから再読み込みする
 */
export const reloadWithTranslationDisabled = () => {
  try {
    sessionStorage.setItem(RECOVERY_KEY, '1');
  } catch (e) {
    // sessionStorage が使えない場合は印を残さず再読み込みするだけ
  }
  window.location.reload();
};

/**
 * 復帰用の再読み込みだった場合、ページ全体を翻訳対象外にする
 *
 * アプリの描画前に一度だけ呼ぶ
 */
export const applyTranslationRecovery = () => {
  try {
    if (!sessionStorage.getItem(RECOVERY_KEY)) return false;
    sessionStorage.removeItem(RECOVERY_KEY);
  } catch (e) {
    return false;
  }
  document.documentElement.setAttribute('translate', 'no');
  return true;
};
